// src/utils/recurrence.js
// VERIFIER: r1-rec-engine-helper-a4f7c2e9
//
// Shared recurrence-projection engine.
// Extracted verbatim from the CalendarView.js projection loop so that the
// Jobs list / Kanban (JobManagementView) and the Calendar project recurring
// occurrences through ONE code path instead of duplicating the date math.
//
// Pure function. No React, no Firebase. Depends only on date-fns.
//
// CONTRACT:
//   - Caller passes template.startAt / template.endAt / template.recurrence.endDate
//     as JS Date objects. This helper does NOT call .toDate(). (CalendarView's
//     subscription already converts; JobManagementView's call site converts before
//     passing — see that view's usage.)
//   - realInstances: the caller's raw jobs array. The ENGINE builds the dedup
//     set from it (was: caller-built existingKeys) so the projection key and the
//     instance-dedup key can never drift in precision again. Each real recurring
//     instance is keyed 'recurringJobId_yyyy-MM-dd' — series-scoped, day-level, in
//     the viewer's local zone. No status filter: Canceled instances stay in the
//     set (keyed on recurringJobId && startAt only) so a canceled occurrence never
//     re-projects as a phantom.
//   - tz: the company's zone (companySettings.timezone). orgtime-7c2e4b19 — a series runs THROUGH
//     its end date's COMPANY day, the same rule the server generators use: an occurrence is kept
//     while its own company day is on or before the end date's. (Was a strict "before the stored
//     end instant", which dropped a visit ON the end date.) No zone → Toronto.
//   - PAST DAYS (orgtime-7c2e4b19, Marcus 2026-10-18): no occurrence is projected on a COMPANY day
//     before the company's today. Spatchy never creates a visit in the past, so a projection there
//     would show a visit that can never exist. Real (created) visits on past days are untouched —
//     they are the caller's realInstances, never projections. Today's occurrence is kept even if
//     its hour has passed. `now` is for tests; callers leave it out.
//   - TIMES (tzhour-6a3d9c58): each projection is the visit the SERVER will create, to the minute,
//     so a projection never jumps an hour when it becomes a real visit. The server's rule is the
//     company's switch (companySettings.visitWallClockEnabled, set only by repairVisitHours.js):
//       * off (every company by default), or a zone that stops changing on 2026-11-01 (Vancouver,
//         Edmonton, Inuvik, Winnipeg — never wall-clock): the OLD stepping — the series' UTC hour
//         kept on each step of its UTC calendar day. After a clock change that is an hour from the
//         saved time in a still-changing zone; in the four zones it is the hour their visits have
//         shown since spring.
//       * on: the series' own clock time on each COMPANY day (orgAtTime), across clock changes.
//     Mirrors recurringOccurrences + visitWallClockFor in functions/index.js (testOrgTime.js W*
//     runs this file against the server's code). Was: the browser's own clock, which matched
//     neither. Callers pass visitWallClockEnabled from the org doc.
//   - windowStart / windowEnd: optional Date bounds.
//       * both null  -> expand from each template's start to now + 2 years
//                       (the original CalendarView behavior).
//       * both set   -> only occurrences whose jobStart falls within
//                       [windowStart, windowEnd] are returned.
//
// RETURNS: array of neutral occurrence objects:
//   { template, occurrenceDate: Date, jobStart: Date, jobEnd: Date }
// Each caller maps these to its own shape (calendar event vs. job row).

import { add, startOfDay, isBefore, format } from 'date-fns';
import { orgYmd, orgWallClock, orgAtTime, resolveOrgTimeZone } from './orgTime'; // orgtime-7c2e4b19 / tzhour-6a3d9c58

// Advance a recurrence cursor by ONE step. Kept byte-identical (minus import
// style) with functions/index.js and the mobile mirror so all engines step the
// lattice the same way. Presets unchanged (Monthly keeps its month-end
// degradation on purpose). Custom months/years re-pin to the anchor
// day-of-month (Jan 31 -> Feb 28 -> Mar 31, non-degrading). Stateless — needs
// no occurrence index. Returns null for an unknown/malformed rule.
function daysInMonthFor(year, monthIdx) {
    return new Date(year, monthIdx + 1, 0).getDate();
}
export function advanceRecurrence(cursor, recurrence, anchor) {
    const freq = recurrence && recurrence.frequency;
    switch (freq) {
        case 'Weekly':    return add(cursor, { days: 7 });
        case 'Bi-Weekly': return add(cursor, { days: 14 });
        case 'Monthly':   return add(cursor, { months: 1 });
        case 'Custom': {
            const n = Math.max(1, parseInt(recurrence.interval, 10) || 1);
            switch (recurrence.unit) {
                case 'days':  return add(cursor, { days: n });
                case 'weeks': return add(cursor, { weeks: n });
                case 'months': {
                    const t = add(cursor, { months: n });
                    const dom = anchor ? anchor.getDate() : cursor.getDate();
                    t.setDate(Math.min(dom, daysInMonthFor(t.getFullYear(), t.getMonth())));
                    return t;
                }
                case 'years': {
                    const dom = anchor ? anchor.getDate() : cursor.getDate();
                    const mon = anchor ? anchor.getMonth() : cursor.getMonth();
                    const year = cursor.getFullYear() + n;
                    const t = new Date(cursor);
                    t.setFullYear(year, mon, Math.min(dom, daysInMonthFor(year, mon)));
                    return t;
                }
                default: return null;
            }
        }
        default: return null;
    }
}

// tzhour-6a3d9c58 — the server's per-company switch (functions/index.js visitWallClockFor). The
// server also needs its tz rules check to pass; it does in production (checkServerTz.js), so the
// apps assume it.
export const FIXED_FROM_2026_ZONES = ['America/Vancouver', 'America/Edmonton', 'America/Inuvik', 'America/Winnipeg'];
export function visitWallClockOn(visitWallClockEnabled, tz) {
    return visitWallClockEnabled === true && !FIXED_FROM_2026_ZONES.includes(resolveOrgTimeZone(tz));
}

// tzhour-6a3d9c58 — a series' occurrences as the server creates them: { legacy, start, end }.
// Calendar days are stepped by advanceRecurrence on a local-NOON date standing for the day (noon
// never falls in a clock-change gap), which is the same calendar arithmetic the server does on UTC.
//   legacy chain: the series' UTC calendar day, stepped, + its UTC time of day (server: startAt
//                 stepped with the process clock in UTC).
//   wall chain:   the series' COMPANY day, stepped, at its company clock time (orgAtTime).
//   end:          the start + the series' duration in both (vendtime-2b7d4f91, as the server).
const noonOf = (ymd) => { const [y, m, d] = ymd.split('-').map(Number); return new Date(y, m - 1, d, 12, 0, 0, 0); };
const ymdOfNoon = (d) => format(d, 'yyyy-MM-dd');
const utcYmd = (d) => d.toISOString().slice(0, 10);
const utcMidnight = (ymd) => Date.parse(`${ymd}T00:00:00Z`);
export function* seriesOccurrences(template, tz, wallClock) {
    const { startAt, endAt, recurrence } = template;
    const zone = resolveOrgTimeZone(tz);
    const legacyTime = startAt.getTime() - utcMidnight(utcYmd(startAt));
    const legacyAnchor = noonOf(utcYmd(startAt));
    const startYmd = orgYmd(startAt, zone);
    const wall = orgWallClock(startAt, zone);
    const durationMs = Math.max(0, endAt.getTime() - startAt.getTime()); // vendtime-2b7d4f91
    const dayAnchor = noonOf(startYmd);
    let legacyDay = legacyAnchor; let day = dayAnchor;
    for (let i = 0; i < 20000; i += 1) {
        const lYmd = ymdOfNoon(legacyDay);
        const legacy = new Date(utcMidnight(lYmd) + legacyTime);
        const start = wallClock ? orgAtTime(ymdOfNoon(day), wall.h, wall.mi, zone) : legacy;
        const end = new Date(start.getTime() + durationMs);
        yield { legacy, start, end };
        const nextLegacy = advanceRecurrence(legacyDay, recurrence, legacyAnchor);
        const nextDay = advanceRecurrence(day, recurrence, dayAnchor);
        if (!nextLegacy || !nextDay) return; // unknown/malformed rule — stop safely
        legacyDay = nextLegacy; day = nextDay;
    }
}

export function projectRecurringOccurrences({
    templates,
    realInstances = [],
    windowStart = null,
    windowEnd = null,
    tz = null,
    now = null,
    visitWallClockEnabled = false, // tzhour-6a3d9c58 — companySettings.visitWallClockEnabled
}) {
    const occurrences = [];
    if (!Array.isArray(templates) || templates.length === 0) return occurrences;

    const hasWindow = !!(windowStart && windowEnd);
    const todayYmd = orgYmd(now || new Date(), tz); // orgtime-7c2e4b19 — the company's today
    const projectionLimit = add(new Date(), { years: 2 });
    const wallClock = visitWallClockOn(visitWallClockEnabled, tz); // tzhour-6a3d9c58

    // Build the dedup set from the real instances the caller passed. Series-scoped
    // day key ('recurringJobId_yyyy-MM-dd') in the viewer's local zone. NO status
    // filter — a Canceled instance still carries recurringJobId + startAt and must
    // stay in the set so its slot is not re-projected as a phantom.
    const toDate = (v) => (v?.toDate ? v.toDate() : (v instanceof Date ? v : null));
    const existingKeys = new Set(
        (Array.isArray(realInstances) ? realInstances : [])
            .filter(j => j.recurringJobId && j.startAt)
            .map(j => {
                const d = toDate(j.startAt);
                return d ? `${j.recurringJobId}_${format(d, 'yyyy-MM-dd')}` : null;
            })
            .filter(Boolean)
    );

    templates.forEach(template => {
        if (
            !template.startAt ||
            !template.endAt ||
            !template.recurrence ||
            template.recurrence.frequency === 'Never'
        ) {
            return;
        }

        const endYmd = template.recurrence.endDate ? orgYmd(template.recurrence.endDate, tz) : null; // orgtime-7c2e4b19

        // tzhour-6a3d9c58 — the server's own instants (see seriesOccurrences).
        for (const { start: jobStart, end: jobEnd } of seriesOccurrences(template, tz, wallClock)) {
            if (!isBefore(jobStart, projectionLimit)) break;
            if (endYmd && orgYmd(jobStart, tz) > endYmd) break; // orgtime-7c2e4b19 — past the end date's company day

            // Keys stay what they were: the occurrence's day in the viewer's zone.
            const eventKey = `${template.id}_${format(jobStart, 'yyyy-MM-dd')}`;

            // Skip dates explicitly marked as exceptions (e.g. rescheduled via drag).
            const dateStr = format(jobStart, 'yyyy-MM-dd');
            const isException = template.exceptions?.includes(dateStr);

            // Window bound: when a window is supplied, only keep occurrences inside it.
            const inWindow = !hasWindow || (jobStart >= windowStart && jobStart <= windowEnd);

            const onPastDay = orgYmd(jobStart, tz) < todayYmd; // orgtime-7c2e4b19

            if (inWindow && !isException && !onPastDay && !existingKeys.has(eventKey)) {
                occurrences.push({
                    template,
                    occurrenceDate: startOfDay(jobStart),
                    jobStart,
                    jobEnd,
                });
            }
        }
    });

    return occurrences;
}